import React, { useState, useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Scroll from "react-scroll";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTachometer,
  faVideo,
  faArrowLeft,
  faEye,
  faPencil,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { useRecoilState } from "recoil";

import { getCMSVideoDetailAPI, putCMSVideoUpdateAPI } from "../../API/CMSVideo";
import FormErrorBox from "../Reusable/FormErrorBox";
import FormInputField from "../Reusable/FormInputField";

import { topAlertMessageState, topAlertStatusState } from "../../AppState";
import PageLoadingContent from "../Reusable/PageLoadingContent";

function VideoUpdate() {
  ////
  //// URL Parameters.
  ////

  const { id } = useParams();

  ////
  //// Global state.
  ////

  const [topAlertMessage, setTopAlertMessage] =
    useRecoilState(topAlertMessageState);
  const [topAlertStatus, setTopAlertStatus] =
    useRecoilState(topAlertStatusState);

  ////
  //// Component states.
  ////

  const [errors, setErrors] = useState({});
  const [isFetching, setFetching] = useState(false);
  const [forceURL, setForceURL] = useState("");
  const [video, setVideo] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  ////
  //// Event handling.
  ////

  const onHandleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const onNameChange = (e) => {
    setName(e.target.value);
  };

  const onDescriptionChange = (e) => {
    setDescription(e.target.value);
  };

  const onSubmitClick = (e) => {
    console.log("onSubmitClick: Starting...");
    setFetching(true);

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("name", name);
    formData.append("description", description);

    putCMSVideoUpdateAPI(
      id,
      formData,
      onCMSVideoUpdateSuccess,
      onCMSVideoUpdateError,
      onCMSVideoUpdateDone
    );
  };

  ////
  //// API.
  ////

  function onCMSVideoUpdateSuccess(response) {
    // For debugging purposes only.
    console.log("onCMSVideoUpdateSuccess: Starting...");
    console.log(response);

    // Add a temporary banner message in the app and then clear itself after 2 seconds.
    setTopAlertMessage("Video updated");
    setTopAlertStatus("success");
    setTimeout(() => {
      console.log("onCMSVideoUpdateSuccess: Delayed for 2 seconds.");
      console.log(
        "onCMSVideoUpdateSuccess: topAlertMessage, topAlertStatus:",
        topAlertMessage,
        topAlertStatus
      );
      setTopAlertMessage("");
    }, 2000);

    // Redirect the user to a new page.
    setForceURL("/cms-video/" + response.id);
  }

  function onCMSVideoUpdateError(apiErr) {
    console.log("onCMSVideoUpdateError: Starting...");
    setErrors(apiErr);

    // Add a temporary banner message in the app and then clear itself after 2 seconds.
    setTopAlertMessage("Failed submitting");
    setTopAlertStatus("danger");
    setTimeout(() => {
      console.log("onCMSVideoUpdateError: Delayed for 2 seconds.");
      console.log(
        "onCMSVideoUpdateError: topAlertMessage, topAlertStatus:",
        topAlertMessage,
        topAlertStatus
      );
      setTopAlertMessage("");
    }, 2000);

    // The following code will cause the screen to scroll to the top of
    // the page. Please see ``react-scroll`` for more information:
    // https://github.com/fisshy/react-scroll
    var scroll = Scroll.animateScroll;
    scroll.scrollToTop();
  }

  function onCMSVideoUpdateDone() {
    console.log("onCMSVideoUpdateDone: Starting...");
    setFetching(false);
  }

  function onVideoDetailSuccess(response) {
    console.log("onVideoDetailSuccess: Starting...");
    setVideo(response);
    setName(response.name);
    setDescription(response.description);
  }

  function onVideoDetailError(apiErr) {
    console.log("onVideoDetailError: Starting...");
    setErrors(apiErr);

    // The following code will cause the screen to scroll to the top of
    // the page. Please see ``react-scroll`` for more information:
    // https://github.com/fisshy/react-scroll
    var scroll = Scroll.animateScroll;
    scroll.scrollToTop();
  }

  function onVideoDetailDone() {
    console.log("onVideoDetailDone: Starting...");
    setFetching(false);
  }

  ////
  //// Misc.
  ////

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      window.scrollTo(0, 0); // Start the page at the top of the page.

      setFetching(true);
      getCMSVideoDetailAPI(
        id,
        onVideoDetailSuccess,
        onVideoDetailError,
        onVideoDetailDone
      );
    }

    return () => {
      mounted = false;
    };
  }, [id]);

  ////
  //// Component rendering.
  ////

  if (forceURL !== "") {
    return <Navigate to={forceURL} />;
  }

  return (
    <>
      <div class="container">
        <section class="section">
          {/* Desktop Breadcrumbs */}
          <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
            <ul>
              <li>
                <Link to="/dashboard">
                  <FontAwesomeIcon className="fas" icon={faTachometer} />
                  &nbsp;Dashboard
                </Link>
              </li>
              <li class="">
                <Link to="/cms-videos" aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faVideo} />
                  &nbsp;Videos
                </Link>
              </li>
              <li class="">
                <Link to={`/cms-video/${id}`} aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faEye} />
                  &nbsp;Details
                </Link>
              </li>
              <li class="is-active">
                <Link aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faPencil} />
                  &nbsp;Edit
                </Link>
              </li>
            </ul>
          </nav>

          {/* Mobile Breadcrumbs */}
          <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
            <ul>
              <li class="">
                <Link to={`/cms-video/${id}`} aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                  &nbsp;Back to Details
                </Link>
              </li>
            </ul>
          </nav>

          {/* Page */}
          <nav class="box">
            <p class="title is-4">
              <FontAwesomeIcon className="fas" icon={faVideo} />
              &nbsp;Video
            </p>
            {isFetching ? (
              <>
                <PageLoadingContent displayMessage={"Loading..."} />
              </>
            ) : (
              <>
                <p class="subtitle is-6">Edit</p>
                <FormErrorBox errors={errors} />

                {video && (
                  <div class="container">
                    <FormInputField
                      label="Name"
                      name="name"
                      placeholder="Text input"
                      value={name}
                      errorText={errors && errors.name}
                      helpText=""
                      onChange={onNameChange}
                      isRequired={true}
                      maxWidth="150px"
                    />

                    <FormInputField
                      label="Description"
                      name="description"
                      type="text"
                      placeholder="Text input"
                      value={description}
                      errorText={errors && errors.description}
                      helpText=""
                      onChange={onDescriptionChange}
                      isRequired={true}
                      maxWidth="485px"
                    />

                    <input type="file" onChange={onHandleFileChange} />
                    <br />
                    <br />

                    <article class="message is-warning">
                      <div class="message-body">
                        Note: Submitting will cause previous video to be
                        deleted.
                      </div>
                    </article>
                    <div class="columns">
                      <div class="column is-half">
                        <Link
                          to={`/cms-video/${video.id}`}
                          class="button is-hidden-touch"
                        >
                          <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                          &nbsp;Back
                        </Link>
                        <Link
                          to={`/cms-video/${video.id}`}
                          class="button is-fullwidth is-hidden-desktop"
                        >
                          <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                          &nbsp;Back
                        </Link>
                      </div>
                      <div class="column is-half has-text-right">
                        <button
                          class="button is-primary is-hidden-touch"
                          onClick={onSubmitClick}
                        >
                          <FontAwesomeIcon className="fas" icon={faCheck} />
                          &nbsp;Save
                        </button>
                        <button
                          class="button is-primary is-fullwidth is-hidden-desktop"
                          onClick={onSubmitClick}
                        >
                          <FontAwesomeIcon className="fas" icon={faCheck} />
                          &nbsp;Save
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </nav>
        </section>
      </div>
    </>
  );
}

export default VideoUpdate;
